import type { ConstraintTableColumn } from '../../../config/optimizePlanConfig';

// Resolves a potentially nested field from an object using dot-notation (e.g. 'constraint.min').
export function getNestedValue(obj: Record<string, unknown>, path: string): unknown {
    return path.split('.').reduce(
        (acc, key) => (acc != null && typeof acc === 'object') ? (acc as Record<string, unknown>)[key] : undefined,
        obj as unknown,
    );
}

// Builds a fields bag from a level_detail record by reading each data column's key from the API response.
export function buildRowFields(levelDetail: Record<string, unknown>, dataColumns: ConstraintTableColumn[]): Record<string, unknown> {
    const fields: Record<string, unknown> = {};
    for (const col of dataColumns) {
        fields[col.key] = getNestedValue(levelDetail, col.key);
    }
    return fields;
}

const ROUND_PRECISION = 2;

function round(value: number, precision: number): number {
    const factor = 10 ** precision;
    return Math.round(value * factor) / factor;
}

/**
 * Computes a symmetric min/max range around `value` using the given percentage (1–100).
 * e.g. `deltaPercent = 20` → ±20% of `value`.
 * Returns `{ min_value: null, max_value: null }` when `deltaPercent` is null or the value is not a finite number.
 */
export function computeDefaultRange(
    value: unknown,
    deltaPercent: number | null,
): { min_value: number | null; max_value: number | null } {
    if (deltaPercent == null || typeof value !== 'number' || !Number.isFinite(value)) {
        return { min_value: null, max_value: null };
    }
    const delta = Math.abs(value) * (deltaPercent / 100);
    return {
        min_value: round(value - delta, ROUND_PRECISION),
        max_value: round(value + delta, ROUND_PRECISION),
    };
}
